import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { SMS_CONSENT } from "@/data";
import { submitLead, apiErr } from "@/lib/api";

// steps: [{ key, question, help, type: 'options'|'multi'|'text'|'contact', options?, placeholder? }]
export function Questionnaire({ steps, leadType, source, onComplete }) {
  const [i, setI] = useState(0);
  const [ans, setAns] = useState({});
  const [consent, setConsent] = useState(false);
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);
  const step = steps[i];
  const pct = Math.round(((i + 1) / steps.length) * 100);

  const setVal = (v) => setAns((a) => ({ ...a, [step.key]: v }));
  const toggleMulti = (v) => setAns((a) => {
    const cur = a[step.key] || [];
    return { ...a, [step.key]: cur.includes(v) ? cur.filter((x) => x !== v) : [...cur, v] };
  });

  const next = async () => {
    setErr("");
    if (step.type === "contact") {
      const c = ans[step.key] || {};
      if (!c.name || !c.email) { setErr("Name and email are required."); return; }
      setLoading(true);
      const details = {};
      steps.forEach((s) => {
        if (s.type === "contact") return;
        const v = ans[s.key];
        if (v !== undefined && v !== "" && !(Array.isArray(v) && v.length === 0)) details[s.question] = Array.isArray(v) ? v.join(", ") : v;
      });
      try {
        await submitLead({
          lead_type: leadType, name: c.name, email: c.email, phone: c.phone || "",
          source_page: source,
          county: Array.isArray(ans.counties) ? ans.counties.join(", ") : (ans.county || ""),
          city: ans.cities || ans.city || "",
          price_range: ans.price_range || ans.desired_price || "",
          timeline: ans.timeline || ans.sell_timeline || "",
          preapproved: ans.preapproved || "",
          contact_preference: ans.contact_pref || c.pref || "Email",
          sms_consent: consent, details,
        });
        onComplete(ans);
      } catch (e) { setErr(apiErr(e.response?.data?.detail)); }
      finally { setLoading(false); }
      return;
    }
    if (i < steps.length - 1) setI(i + 1);
  };

  return (
    <div className="min-h-screen bg-cream flex flex-col pt-24 pb-32 px-6">
      <div className="max-w-2xl mx-auto w-full">
        <div className="h-1.5 bg-navy/10 rounded-full mb-2 overflow-hidden">
          <motion.div className="h-full bg-gold" animate={{ width: `${pct}%` }} data-testid="quiz-progress" />
        </div>
        <p className="font-mono text-xs text-charcoal/50 mb-12">Question {i + 1} of {steps.length}</p>
        <AnimatePresence mode="wait">
          <motion.div key={i} initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -40 }} transition={{ duration: 0.35 }}>
            <h2 className="font-serif text-3xl md:text-4xl text-navy tracking-tight mb-2" data-testid="quiz-question">{step.question}</h2>
            {step.help && <p className="text-charcoal/60 mb-8">{step.help}</p>}

            {step.type === "text" && (
              <Input value={ans[step.key] || ""} onChange={(e) => setVal(e.target.value)} placeholder={step.placeholder}
                className="text-xl border-0 border-b-2 border-black/10 rounded-none px-0 focus-visible:ring-0 focus:border-gold bg-transparent" data-testid="quiz-text" />
            )}

            {(step.type === "options" || step.type === "multi") && (
              <div className="grid sm:grid-cols-2 gap-3 mt-6">
                {step.options.map((o) => {
                  const active = step.type === "multi" ? (ans[step.key] || []).includes(o) : ans[step.key] === o;
                  return (
                    <button key={o} data-testid={`quiz-opt-${o.toLowerCase().replace(/[^a-z0-9]/g, "-")}`}
                      onClick={() => step.type === "multi" ? toggleMulti(o) : setVal(o)}
                      className={`text-left px-5 py-4 rounded-2xl border transition-colors ${active ? "bg-navy text-cream border-navy" : "bg-white border-black/10 text-charcoal hover:border-gold"}`}>
                      {o}
                    </button>
                  );
                })}
              </div>
            )}

            {step.type === "contact" && (
              <div className="space-y-4 mt-6">
                <Input placeholder="Full name" value={ans[step.key]?.name || ""} data-testid="quiz-contact-name"
                  onChange={(e) => setVal({ ...(ans[step.key] || {}), name: e.target.value })} className="bg-white" />
                <Input placeholder="Email" type="email" value={ans[step.key]?.email || ""} data-testid="quiz-contact-email"
                  onChange={(e) => setVal({ ...(ans[step.key] || {}), email: e.target.value })} className="bg-white" />
                <Input placeholder="Phone" value={ans[step.key]?.phone || ""} data-testid="quiz-contact-phone"
                  onChange={(e) => setVal({ ...(ans[step.key] || {}), phone: e.target.value })} className="bg-white" />
                <div className="flex items-start gap-3 pt-2">
                  <Checkbox id="q-consent" checked={consent} onCheckedChange={setConsent} data-testid="quiz-consent" className="mt-1" />
                  <label htmlFor="q-consent" className="text-xs text-charcoal/60 leading-relaxed">{SMS_CONSENT}</label>
                </div>
              </div>
            )}

            {err && <p className="text-destructive text-sm mt-4" data-testid="quiz-error">{err}</p>}
          </motion.div>
        </AnimatePresence>

        <div className="flex items-center justify-between mt-12">
          <button onClick={() => setI(Math.max(0, i - 1))} disabled={i === 0} data-testid="quiz-back"
            className="inline-flex items-center gap-2 text-charcoal/60 disabled:opacity-30 hover:text-navy transition-colors">
            <ArrowLeft size={18} /> Back
          </button>
          <Button onClick={next} disabled={loading} data-testid="quiz-next"
            className="bg-gold text-navy font-semibold rounded-full px-8 h-12 hover:bg-gold/90">
            {loading ? "Submitting…" : step.type === "contact" ? "See My Results" : "Continue"} {!loading && step.type !== "contact" && <ArrowRight size={18} className="ml-1" />}
          </Button>
        </div>
      </div>
    </div>
  );
}
